import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, ButtonVariants } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { useTranslations, UnitTypeContext, useUnitTypeHeader } from '@/hooks';
import { ROUTES } from '@/app/routes';
import {
  SummaryTiles,
  LinkOrdersForShipment,
  buildOutgoingShipmentLinkFields,
} from '@/components/common';
import { buildTableLabels } from '@/config/tableLabels';
import { EMPTY_LIST } from '@/lib/utils';
import { formatQuantityOrDash } from '@/lib/format';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import {
  useGetShipmentLinkedOrdersQuery,
  useCreateShipmentForOrderMutation,
} from '../api/outgoingShipmentsApi';
import { UpdateShipmentModal } from './UpdateShipmentModal';
import { buildShipmentLinkedOrdersColumns } from '../constants/shipmentLinkedOrdersColumns';
import { buildUnitHeaderOptions } from '../constants/outgoingShipmentUnits';

const getLinkedOrderId = (row) => row.id;

/**
 * Props:
 *   shipmentId  the shipment's database id
 *   shipment    the shipment detail view model — its quantities, match ids and unassigned ceiling
 */
export const LinkedOrdersTabView = ({ shipmentId, shipment }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module } = useParams();
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [sort, setSort] = useState(null);
  const [updateRow, setUpdateRow] = useState(null);
  const [createShipmentForOrder, { isLoading: isCreating }] = useCreateShipmentForOrderMutation();
  const shipmentByUnit = shipment?.byUnit;
  const { data: rows = EMPTY_LIST, isFetching } = useGetShipmentLinkedOrdersQuery({
    id: shipmentId,
    sort,
  });

  const { unitHeaderSelect, contextValue } = useUnitTypeHeader(rows, buildUnitHeaderOptions, t);

  // A row is a pending order, so Fulfill lands on that slice's own route.
  const handleFulfill = useCallback(
    (row) => navigate(ROUTES.orderFulfill(module, row.id)),
    [navigate, module],
  );

  const handleLinkOrders = useCallback(() => setIsLinkOpen(true), []);
  const handleCloseLink = useCallback(() => setIsLinkOpen(false), []);

  // Creates a NEW shipment from this order — it does not edit the shipment being viewed.
  const handleUpdateShipment = useCallback((row) => setUpdateRow(row), []);
  const handleCloseUpdate = useCallback(() => setUpdateRow(null), []);

  const handleSubmitUpdate = useCallback(
    async (values) => {
      const result = await createShipmentForOrder({
        module,
        orderId: updateRow?.id,
        shipmentId,
        values,
      });
      if (result.error) return;
      setUpdateRow(null);
    },
    [createShipmentForOrder, module, updateRow, shipmentId],
  );

  const handleViewOrder = useCallback(
    (row) => {
      navigate(ROUTES.orderDetail(module, row.id));
    },
    [navigate, module],
  );

  const rowActions = useMemo(
    () => [
      {
        id: 'fulfill',
        label: t('ACTION_FULFILL'),
        variant: ButtonVariants.tertiary,
        onClick: handleFulfill,
      },
      {
        id: 'update-shipment',
        label: t('ACTION_UPDATE_SHIPMENT'),
        variant: ButtonVariants.tertiary,
        onClick: handleUpdateShipment,
      },
    ],
    [t, handleFulfill, handleUpdateShipment],
  );

  const rowMenuActions = useMemo(
    () => [{ id: 'view', label: t('COMMON_VIEW'), onClick: handleViewOrder }],
    [t, handleViewOrder],
  );

  const columns = useMemo(
    () => buildShipmentLinkedOrdersColumns(t, { unitHeaderSelect }),
    [t, unitHeaderSelect],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const toolbarActions = useMemo(
    () => [
      {
        id: 'link-orders',
        label: t('OUTGOING_SHIPMENTS_ACTION_LINK_ORDERS'),
        icon: Plus,
        variant: ButtonVariants.primary,
        onClick: handleLinkOrders,
      },
    ],
    [t, handleLinkOrders],
  );

  const tiles = useMemo(() => {
    const unitValues = shipmentByUnit?.[unitHeaderSelect.value];
    if (!unitValues) return [];
    const raw = shipment?.rawByUnit?.[unitHeaderSelect.value];
    const assigned =
      raw?.shipmentQuantity != null && raw?.unassignedQuantity != null
        ? raw.shipmentQuantity - raw.unassignedQuantity
        : null;
    const unit = { unit: unitValues.unit };
    return [
      {
        label: t('INCOMING_SHIPMENTS_LINKED_CONTRACTS_SUMMARY_ASSIGNED', unit),
        value: formatQuantityOrDash(assigned, unitValues.unit),
      },
      {
        label: t('INCOMING_SHIPMENTS_LINKED_CONTRACTS_SUMMARY_UNASSIGNED', unit),
        value: unitValues.unassignedQuantity,
      },
    ];
  }, [shipmentByUnit, shipment, unitHeaderSelect, t]);

  const linkDetailFields = useCallback(
    (unitType) => buildOutgoingShipmentLinkFields(t, shipment, unitType),
    [t, shipment],
  );
  // The wizard takes a list for both flows; this page links orders onto exactly one shipment.
  const linkShipments = useMemo(() => (shipment ? [shipment] : EMPTY_LIST), [shipment]);

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('OUTGOING_SHIPMENTS_DETAIL_ORDERS_EMPTY_TITLE'),
      description: t('OUTGOING_SHIPMENTS_DETAIL_ORDERS_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  return (
    <SummaryTiles tiles={tiles}>
      <UnitTypeContext.Provider value={contextValue}>
        <Table
          columns={columns}
          data={rows}
          loading={isFetching}
          getRowId={getLinkedOrderId}
          searchable
          onSortChange={setSort}
          labels={tableLabels}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          rowMenuActions={rowMenuActions}
          actionsHeader={t('FIELD_ACTION')}
          emptyState={emptyState}
        />
      </UnitTypeContext.Provider>

      <UpdateShipmentModal
        open={updateRow != null}
        onClose={handleCloseUpdate}
        order={updateRow}
        unitSymbol={updateRow?.byUnit?.[unitHeaderSelect.value]?.unit}
        isSaving={isCreating}
        onSubmit={handleSubmitUpdate}
      />

      <LinkOrdersForShipment
        module={module}
        shipments={linkShipments}
        detailFields={linkDetailFields}
        open={isLinkOpen}
        onClose={handleCloseLink}
      />
    </SummaryTiles>
  );
};
